/**
 * "Add to calendar" deep links for the web calendars people actually use.
 *
 * Each provider gets the draft's wall-clock time plus its zone name where the
 * provider supports one (Google's `ctz`); the rest receive floating times and
 * interpret them in the signed-in user's zone. We never convert zones ourselves.
 */

import { type EventDraft, effectiveEnd, timeClock, timeDate, draftTimeZone } from './model';
import { LIMITS, sanitizeEmail, sanitizeLine, sanitizeText, sanitizeUrl } from './sanitize';

export type CalendarProvider = 'google' | 'outlook' | 'office365' | 'yahoo';

export interface CalendarLink {
  readonly provider: CalendarProvider;
  readonly label: string;
  readonly url: string;
}

function addDays(date: string, days: number): string {
  const [year = 0, month = 1, day = 1] = date.split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** `20260815T090000` (or `20260815` all-day), with `Z` when the draft is in UTC. */
function compact(draft: EventDraft, which: 'start' | 'end'): string {
  const time = which === 'start' ? draft.start : effectiveEnd(draft);
  const date = time.kind === 'date' ? addDays(time.date, which === 'end' ? 1 : 0) : timeDate(time);
  const compactDate = date.replace(/-/g, '');
  const clock = timeClock(time);
  if (clock === null) return compactDate;
  return `${compactDate}T${clock.replace(/:/g, '')}${draftTimeZone(draft) === 'UTC' ? 'Z' : ''}`;
}

/** `2026-08-15T09:00:00` (or `2026-08-15` all-day) — the form Outlook and Yahoo take. */
function iso(draft: EventDraft, which: 'start' | 'end'): string {
  const time = which === 'start' ? draft.start : effectiveEnd(draft);
  if (time.kind === 'date') return addDays(time.date, which === 'end' ? 1 : 0);
  return `${timeDate(time)}T${timeClock(time) ?? '00:00:00'}`;
}

function query(params: Record<string, string | undefined>): string {
  return Object.entries(params)
    .filter((entry): entry is [string, string] => entry[1] !== undefined && entry[1] !== '')
    .map(([key, value]) => `${key}=${encodeURIComponent(value)}`)
    .join('&');
}

/**
 * Description plus the event URL, since only some providers have a URL field.
 *
 * Every value here is percent-encoded by `query()`, so a hostile string can't add a
 * parameter — but it is still clamped, and a non-http(s) URL is dropped rather than handed
 * to a provider that might render it.
 */
function details(draft: EventDraft): string | undefined {
  const description =
    draft.description === undefined ? '' : sanitizeText(draft.description, LIMITS.description);
  const url = draft.url === undefined ? null : sanitizeUrl(draft.url);
  const parts = [description, url ?? ''].filter((part) => part !== '');
  return parts.length === 0 ? undefined : parts.join('\n\n');
}

const location = (draft: EventDraft): string | undefined =>
  draft.location === undefined ? undefined : sanitizeLine(draft.location, LIMITS.location);

export function googleCalendarUrl(draft: EventDraft): string {
  const zone = draftTimeZone(draft);
  return `https://calendar.google.com/calendar/render?${query({
    action: 'TEMPLATE',
    text: sanitizeLine(draft.title, LIMITS.title),
    dates: `${compact(draft, 'start')}/${compact(draft, 'end')}`,
    ctz: zone === 'UTC' ? undefined : zone,
    details: details(draft),
    location: location(draft),
    add: (draft.attendees ?? [])
      .slice(0, LIMITS.attendees)
      .map((attendee) => sanitizeEmail(attendee.email))
      .filter((email): email is string => email !== null)
      .join(','),
  })}`;
}

function outlookUrl(host: string, draft: EventDraft): string {
  return `https://${host}/calendar/0/deeplink/compose?${query({
    path: '/calendar/action/compose',
    rru: 'addevent',
    subject: sanitizeLine(draft.title, LIMITS.title),
    body: details(draft),
    location: location(draft),
    startdt: iso(draft, 'start'),
    enddt: iso(draft, 'end'),
    allday: draft.start.kind === 'date' ? 'true' : undefined,
  })}`;
}

export function outlookLiveUrl(draft: EventDraft): string {
  return outlookUrl('outlook.live.com', draft);
}

export function office365Url(draft: EventDraft): string {
  return outlookUrl('outlook.office.com', draft);
}

export function yahooCalendarUrl(draft: EventDraft): string {
  const allDay = draft.start.kind === 'date';
  return `https://calendar.yahoo.com/?${query({
    v: '60',
    title: sanitizeLine(draft.title, LIMITS.title),
    st: compact(draft, 'start'),
    et: compact(draft, 'end'),
    dur: allDay ? 'allday' : undefined,
    desc: details(draft),
    in_loc: location(draft),
  })}`;
}

/** Every supported deep link, in the order a picker should show them. */
export function addToCalendarLinks(draft: EventDraft): CalendarLink[] {
  return [
    { provider: 'google', label: 'Google Calendar', url: googleCalendarUrl(draft) },
    { provider: 'outlook', label: 'Outlook.com', url: outlookLiveUrl(draft) },
    { provider: 'office365', label: 'Office 365', url: office365Url(draft) },
    { provider: 'yahoo', label: 'Yahoo Calendar', url: yahooCalendarUrl(draft) },
  ];
}
